import {Paper, Title, Alert, TextInput, Button, Anchor, Modal, Text} from '@mantine/core';
import {useEffect, useState} from 'react';
import {useFormik} from 'formik';

import {authActions} from 'src/store/reducers/auth-reducer';
import useTranslation from 'src/i18next.config';
import {SignInPayload, useAuthService} from 'src/services/authentication';
import {persistor} from 'src/store';
import {useNavigate} from 'react-router-dom';
import {ERoutes} from 'src/types/enum/routes.enum';
import {userProfileActions} from 'src/store/reducers/user-reducer';
import {showNotification} from '@mantine/notifications';
import useTosService from 'src/services/tos';

export const SignIn = () => {

    const authService = useAuthService();
    const saveToken = authActions.useSaveToken();
    const saveProfile = userProfileActions.useSaveProfile();
    const token = authActions.useToken();
    const {t} = useTranslation();
    const {getLatestPublicTos} = useTosService();

    const [loading, setLoading] = useState(false);
    const [errorMessage] = useState<string | null>();
    const [tosModalOpen, setTosModalOpen] = useState(false);
    const [tosContent, setTosContent] = useState<string>('');
    const navigate = useNavigate();

    useEffect(() => {
        persistor.persist();
        setLoading(false);
    }, []);

    useEffect(() => {
        if (token) {
            navigate(ERoutes.ROOT);
        }
    }, []);

    const formik = useFormik<SignInPayload>({
        initialValues: {
            email: '',
            password: '',
        },
        onSubmit: signIn,
    });

    function signIn(signInPayload: SignInPayload) {
        authService.signIn(
            {
                loading: value => setLoading(value),
                error: err => {
                    showNotification({
                        title: t('auth.signInNotification'),
                        message: err.message,
                        color: 'red',
                    });
                },
                success: res => {
                    const {token, refreshToken} = res;
                    saveToken({token, refreshToken});
                    saveProfile(token);
                    navigate(ERoutes.ROOT);
                },
            },
            signInPayload
        );
    }

    function openTosModal() {
        getLatestPublicTos({
            success: (tos: any) => {
                setTosContent(tos?.contents || t('tos.noToSFound'));
                setTosModalOpen(true);
            },
            error: () => {
                setTosContent(t('tos.noToSFound') as string);
                setTosModalOpen(true);
            },
        });
    }

    return (
        <div>
            <Paper
                p="md"
                shadow="md"
                component="form"
                onSubmit={formik.handleSubmit}
                noValidate
                style={{
                    paddingTop: 120,
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    maxWidth: 480,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    marginTop: 50,
                    gap: 20,
                    padding: 24,
                }}
            >
                <Title order={1} style={{textAlign: 'center'}}>MIMIQ</Title>
                <Text size="sm" c="dimmed" style={{textAlign: 'center'}}>by QPerfect</Text>
                <Title order={2}>{t('signIn')}</Title>
                <div>
                    <Title order={6}>{t('email')}</Title>
                    <TextInput
                        id="email"
                        type="email"
                        autoComplete="on"
                        placeholder={t('emailPlaceholder').toString()}
                        onChange={formik.handleChange}
                        value={formik.values.email}
                        error={!!formik.touched.email && !!formik.errors.email && formik.errors.email}
                    />
                </div>
                <div>
                    <Title order={6}>{t('password')}</Title>
                    <TextInput
                        id="password"
                        type="password"
                        placeholder={t('password').toString()}
                        autoComplete="on"
                        onChange={formik.handleChange}
                        value={formik.values.password}
                        error={!!formik.touched.password && !!formik.errors.password && formik.errors.password}
                    />
                </div>
                <Button color="cyan.8" type="submit" loading={loading}>
                    {t('signIn')}
                </Button>
                <Anchor
                    size="sm"
                    style={{textAlign: 'center'}}
                    onClick={openTosModal}
                >
                    {t('tos.tos')}
                </Anchor>
            </Paper>
            {errorMessage && (
                <Alert color="red" radius="lg" variant="filled">
                    {errorMessage}
                </Alert>
            )}
            <Modal
                opened={tosModalOpen}
                onClose={() => setTosModalOpen(false)}
                title={t('tos.tos')}
                size="lg"
            >
                <div
                    style={{overflowX: 'hidden', wordWrap: 'break-word'}}
                    dangerouslySetInnerHTML={{__html: tosContent}}
                />
            </Modal>
        </div>
    );
};

export default SignIn;
